import { useState } from "react";
import Card from "./card";
import { Asset } from "../../../models/asset";
import { Scheduling } from "../../../models/schedule";
import { Loading } from "../../../utils/loading";
import { EmptyNextSchedules } from "./emptySchedules";
import { useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import PeriodicReviewsPdf from "./periodicReviewsPdf";
import Html2Pdf from "js-html2pdf";
import { SignedRevision } from "../../../models/signedRevision";
import { useMediaQuery } from "@mui/material";

interface ICheckupsProps {
  asset: Asset | null;
  blockchainData: (scheduleId: string) => SignedRevision | undefined;
  loading: boolean;
}

const Checkups = ({ asset, blockchainData, loading }: ICheckupsProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const isResponsiveView = useMediaQuery("(max-width: 1023px)");

  /**
     * Divide uma array de Scheduling em subarrays de até 4 elementos cada.
     * @param schedulings Array de Scheduling
     * @returns Array de arrays, cada um com até 4 Scheduling
     */
  function paginateSchedulings(schedulings: Scheduling[]): Scheduling[][] {
    const result: Scheduling[][] = [];
    for (let i = 0; i < schedulings.length; i += 4) {
      result.push(schedulings.slice(i, i + 4));
    }
    return result;
  }

  const downloadPeriodicReviews = useReactToPrint({
    contentRef,
    documentTitle: "Manutenções e Revisões",
    print: async (printIframe) => {
      setDownloading(true);
      const document = printIframe.contentDocument;
      if (document) {
        try {
          const html = document.getElementById("contentRef");
          const exporter = new Html2Pdf(
            html,
            {
              filename: `Manutenções e Revisões - ${asset?.name}.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 2, logging: true, dpi: 192, letterRendering: true },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: { mode: 'css', before: '.page-break' }
            }
          );
          // Generate PDF
          const pdf = await exporter.getPdf(false);

          // Remove last page (blank page) if it exists
          pdf.deletePage(paginateSchedulings([
            ...(asset?.schedulings?.previous ?? []),
            ...(asset?.schedulings?.next ?? []),
          ]).length + 1);

          // Open pdf in new tab
          const blob = pdf.output('blob');
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');

          // Download pdf
          pdf.save(`Manutenções e Revisões - ${asset?.name}.pdf`);

        } catch (error) {
          console.info(error);
        }
        setDownloading(false);
      }
    },
  });

  const [order, setOrder] = useState<string>("down");

  const handleOrderChange = () => setOrder(order === "down" ? "up" : "down");

  return (
    <div className="revision-content pr-5">
      {loading && <div className="flex justify-center items-center h-full">
        <Loading label="Carregando revisões..." />
      </div>}
      {!loading && (
        <div>
          <button
            type="button"
            className="btn btn-default mt-8 w-full sm:w-auto flex items-center justify-center sm:justify-start"
            onClick={() => {
              setDownloading(true);
              downloadPeriodicReviews();
            }}
            disabled={downloading}
          >
            {downloading ? "Baixando..." : "Baixar"}
            {!downloading && (
              <span
                className={`rioglyph rioglyph-download padding-left-10`}
                aria-hidden="true"
              />
            )}
          </button>

          <div className="flex justify-between items-center actions-row mt-10 flex-wrap">
            <div className="mb-2 sm:mb-0">Revisões realizadas</div>
            <div className="form-group w-full sm:w-auto">
              <div className="display-flex gap-10 align-items-end">
                <div className="form-group">
                  <div onClick={handleOrderChange} className="cursor-pointer">
                    {order === "down" ? "Mais antigo" : "Mais recente"}
                    <span className={`rioglyph rioglyph-chevron-${order} padding-left-10`} aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`grid ${isResponsiveView ? 'grid-cols-1' : 'grid-cols-2'} gap-6 scrollable mt-8`}>
            {[
              ...(asset?.schedulings?.previous ?? []),
              ...(asset?.schedulings?.next ?? []),
            ]
              ?.sort(() => order === "down" ? 0 : -1)
              .map((scheduleCheckup: Scheduling) => <Card scheduling={scheduleCheckup} isOilChange={false} isSigned={!!blockchainData(scheduleCheckup.id)?.id} finished={scheduleCheckup.isFinished} />)}
          </div>

          {
            asset?.schedulings?.previous?.length === 0 && asset?.schedulings?.next?.length === 0 && (
              <div className="border rounded-lg p-4 w-full">
                <span className="text-size-14 text-color-dark">Não há revisões realizadas</span>
              </div>)
          }

          {asset?.schedulings?.next?.length === 0 && <EmptyNextSchedules />}

          <div className={`mt-[200px] ${!downloading ? "hidden" : ""}`}>
            <div ref={contentRef} id="contentRef">
              <PeriodicReviewsPdf
                asset={asset}
                blockchainData={blockchainData}
                order={order}
                paginateSchedulings={paginateSchedulings}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkups;
